"use client";
import parse from "html-react-parser";
import ExpandableBlock from "@/components/ContentBlock/variants/ExpandableBlock";
import SplitBackground from "@/components/ContentBlock/variants/SplitBackground";
import Factoid from "@/components/CourseContent/Factoid";
import Section from "@/components/CourseContent/Section";
import Launch from "@/components/icons/Launch";
import type { ProcessedCourse } from "@/common/course/types";
import getSmarterLogo from "@/public/images/getsmarter-logo.svg";
import styles from "./styles.module.css";
import ExecEdEnrollButton from "@/components/CourseContent/ExecEdEnrollButton";
import validateIsFromUniversityOfCapetown from "@/lib/course/utils/isFromUniversityOfCapetown";
import CourseDescription from "../CourseDescription";
import CTALink from "@/components/CTA/ctaLink";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { ExecEdStickyCTATestResult } from "@/monarch/rules/execEdStickyCTA/types";

interface ExecCourseAboutProps {
  course: ProcessedCourse;
  paymentOption: string | null;
  marketingType?: string;
  enrollButtonLink?: URL | string;
  title?: string;
  subject?: string;
}

export default function ExecCourseAbout({
  course,
  paymentOption,
  marketingType,
  enrollButtonLink,
  title,
  subject,
}: ExecCourseAboutProps) {
  const facts =
    course?.additionalMetadata?.[0]?.facts?.map((fact) => ({
      ...fact,
      heading: fact.heading ?? "",
      blurb: fact.blurb ?? "",
    })) ?? [];
  const certificateInfo =
    course?.additionalMetadata?.[0]?.certificateInfo?.filter(
      (item) => item.heading !== null && item.heading !== undefined,
    ) ?? [];
  const certificateHeading =
    course?.additionalMetadata?.[0]?.certificateInfo[0]?.heading ?? "";
  const certificateText =
    course?.additionalMetadata?.[0]?.certificateInfo[0]?.blurb ?? "";
  const isFromUniversityOfCapetown = validateIsFromUniversityOfCapetown(
    course?.partners,
  );
  const externalIdentifier =
    course.additionalMetadata.at(0)?.externalIdentifier;

  const courseTermOverride = course.additionalMetadata[0]?.courseTermOverride;
  const courseCurriculumTitle = courseTermOverride
    ? `${courseTermOverride} curriculum`
    : "Course curriculum";
  const aboutThisCourseTitle = courseTermOverride
    ? `About this ${courseTermOverride}`
    : "About this course";

  // TODO: dynamic href (if needed) when all additionalMetadata is present
  const readAboutGetSmarterBtn = (
    <div
      className="bg-white text-sm text-primary md:text-base font-semibold w-full min-h-11 md:w-auto hover:bg-primary hover:text-white border border-solid rounded-md px-2"
      style={{ outline: "none" }}
    >
      <a
        className="flex flex-row p-2 justify-center"
        href={`https://www.getsmarter.com/why-getsmarter?external_link=true${externalIdentifier ? `&eaid=${externalIdentifier}` : ""}`}
        target="_blank"
      >
        <span className="pr-2">Read more about GetSmarter</span>
        <Launch svgProps={{ width: "24", height: "24" }} />
      </a>
    </div>
  );

  return (
    <>
      <div className={styles.execEdDetailCenteredContent}>
        <Section link="about" title={aboutThisCourseTitle}>
          <CourseDescription
            courseData={course}
            paymentOption={paymentOption}
            video={course.video}
            image={course.productImageUrl}
            title={title}
          />
        </Section>
        {course.syllabusHtml && (
          <Section
            link="curriculum"
            title={courseCurriculumTitle}
            className="mb-10"
            titleClassName="mb-4"
          >
            <ExpandableBlock blur>
              <div className="prose">{parse(course.syllabusHtml)}</div>
            </ExpandableBlock>

            <div className="flex flex-row justify-center md:justify-start w-full md:w-auto pt-10 pb-5 md:pb-10 gap-1 md:gap-5">
              {marketingType === "sprint" && enrollButtonLink && (
                <ExecEdEnrollButton enrollButtonLink={enrollButtonLink} />
              )}
              <MonarchDataConsumer<ExecEdStickyCTATestResult>
                experimentKey="useExecEdStickyCTA"
                render={(testResult) => {
                  const { data } = testResult;
                  return (
                    <CTALink
                      label={data?.bottomCtaText || "Request more info"}
                      url="#brochure"
                      type="brand"
                      enable_tracking={true}
                      className="text-sm md:text-base w-full h-full min-h-11 md:w-auto px-4 py-2"
                      location="mid-page"
                      html_id="request-more-info-cta"
                      name="curriculum-cta"
                    />
                  );
                }}
              />
            </div>
          </Section>
        )}
      </div>
      <div className="mx-auto w-full md:bg-putty-100">
        {certificateInfo.length > 0 && (
          <Section link="certificate" titleMobile="Certificate">
            <SplitBackground
              heading={certificateHeading}
              body={certificateText}
              certificateForUCT={isFromUniversityOfCapetown}
            />
          </Section>
        )}
        {facts.length > 0 && (
          <Section
            link="facts"
            title="Some facts about the subject"
            showSectionTitle={false}
          >
            <Factoid header="Some facts about the subject" facts={facts} />
          </Section>
        )}
        <Section link="whygetsmarter" titleMobile="Why GetSmarter?">
          <SplitBackground
            headingLogo={getSmarterLogo}
            heading="Why choose GetSmarter?"
            showHeadingMobile={false}
            body="<p>GetSmarter, an edX partner, delivers career-relevant, fully supported, and expert-led online courses from the world's leading universities and institutions. As an online learning expert with over 15 years' experience, GetSmarter leverages long-standing expertise in market research, data analysis, and business insights to ensure that you gain the skills critical to career growth.</p>"
            customButton={readAboutGetSmarterBtn}
          />
        </Section>
      </div>
    </>
  );
}
